// A Wixel asset is recognizable from its own URL, which is what makes "Edit in Wixel" offerable on a file
// nothing ever stamped with the FileRecord header's `wixelEditorUrl` (production writes those 4 fields
// nowhere yet — only the dev seed script does). Wixel returns ONE short link per asset and the surface it
// opens is chosen by a trailing segment: bare → preview, `/preview` → the same, `/view` → viewer,
// `/edit` → editor, `/raw` → the exported FILE itself.
//
// ⚠️ `/raw` is the form the SERVER attaches. Since #10580 (2026-09-21) a Wixel asset is attached where it
// lives instead of being copied into the owner's media library, and `rehost-file.ts` rewrites the short link
// to `/raw` on the way — the bare link serves the preview PAGE, so passing it through would attach HTML.
// `raw` was not in this set, so every Wixel asset attached after that date stopped being a Wixel link to all
// three clients: no derived preview image, and no "Edit in Wixel" on any of them. Reported 2026-09-26 as
// "Wixel images not rendering in Symphony chat (mobile and desktop)" — one server-side URL change, so all
// three surfaces at once, which is what "and desktop" in a mobile bug report means.
//
// The picture a client draws is `/raw`, the exported file: a PNG or JPEG when the asset is an image. Wixel's
// link service (`AssetLinksController` in wixel-agent) serves the PAGE on `/preview`, `/view` and the bare
// link, falling back to the file only for an asset it never published, and the FILE on `/raw` — so drawing
// `/preview` puts Wixel's HTML viewer into an image. A non-image asset's export is a PDF or a video, which no
// image view can draw; Wixel's `/poster` route is meant for that, but nothing writes it yet. `/edit` 302s to
// the `manage.wix.com/wixel-new/…/wixel-editor` deep link.
//
// Mirrors native `WixelLink.swift` / `WixelLink.kt` byte-for-byte — the host set, the `/link/<slug>` shape,
// the tolerated trailing segments and the https-only rule are one contract across the three surfaces.

import { parseFileRecordContent } from './file-record';
import { extractUrl } from './artifact-url';

/** Segments Wixel itself appends to a short link. A stored URL may already carry one, so it is replaced
 *  rather than appended to — `/link/abc/view/edit` addresses nothing. */
const SURFACE_SEGMENTS = new Set(['edit', 'view', 'preview', 'raw']);

function isWixelHost(host: string): boolean {
  return host === 'wixel.com' || host.endsWith('.wixel.com');
}

/** `<host>/link/<slug>` for a Wixel short link, or null when `rawUrl` is not one — the single parser both
 *  surface helpers below share, so they can never disagree about what counts as a Wixel asset.
 *
 *  https only: an http link would open the asset over a downgraded connection, and every link Wixel hands
 *  out is https. Query and hash are dropped — a short link carries none, and forwarding a `?token=` loan
 *  onto an editor address would be meaningless. The ONE exception is `v`, returned as `version`: Symphony
 *  adds `?v=<version>` after the user edits the asset in Wixel, and the derived picture keeps it (see
 *  `wixelPreviewUrlFromAssetUrl`). */
function wixelShortLink(rawUrl: string | null | undefined): { host: string; slug: string; version: string | null } | null {
  if (!rawUrl) return null;
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || !isWixelHost(url.hostname.toLowerCase())) return null;
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments[0] !== 'link' || !segments[1]) return null;
  if (segments.length > 3) return null;
  if (segments.length === 3 && !SURFACE_SEGMENTS.has(segments[2].toLowerCase())) return null;
  const version = url.searchParams.get('v')?.trim() || null;
  return { host: url.hostname, slug: segments[1], version };
}

/** Whether `rawUrl` is a Wixel short link — the same parser the edit and preview helpers use, so "is this a
 *  Wixel asset" can never disagree with whether those helpers answer. */
export function isWixelShortLink(rawUrl: string | null | undefined): boolean {
  return wixelShortLink(rawUrl) !== null;
}

/** The editor URL for a Wixel short link, or null when `rawUrl` is not one. */
export function wixelEditUrlFromAssetUrl(rawUrl: string | null | undefined): string | null {
  const link = wixelShortLink(rawUrl);
  return link && `https://${link.host}/link/${link.slug}/edit`;
}

/** The host the Wixel app claims as its universal / App Link (all paths, iOS and Android). Without the app,
 *  it 302s to the JotURL smart link `invite.mobileappinvite.com/wixel?deeplink=<path>`, which routes to the
 *  right store and carries the path as a deferred deep link — so one https link opens the app OR the store. */
const WIXEL_APP_LINK_HOST = 'apps.wixel.com';

/** The Wixel app's universal link for an Edit-in-Wixel editor URL, or null when the URL is neither editor
 *  form (then "Get the Wixel app" falls back to the store page):
 *  - a short link (`https://<*.wixel.com>/link/<slug>[/edit]`) → `https://apps.wixel.com/link/<slug>/edit?ref=symphony`
 *  - the editor deep link (`https://manage.wix.com/wixel-new/<metaSiteId>/wixel-editor/<projectId>?…`) →
 *    `https://apps.wixel.com/editProject/<metaSiteId>/<projectId>?ref=symphony`
 *
 *  An app that does not yet read `/link/<slug>/edit` opens its home for it, which is acceptable. Mirrors native
 *  `WixelEditChoice.appLink` (iOS and Android): same inputs, same output string. */
export function wixelAppLink(editorUrl: string | null | undefined): string | null {
  const short = wixelShortLink(editorUrl);
  if (short) return `https://${WIXEL_APP_LINK_HOST}/link/${short.slug}/edit?ref=symphony`;
  if (!editorUrl) return null;
  let url: URL;
  try {
    url = new URL(editorUrl.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'manage.wix.com') return null;
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length !== 4 || segments[0] !== 'wixel-new' || segments[2] !== 'wixel-editor') return null;
  return `https://${WIXEL_APP_LINK_HOST}/editProject/${segments[1]}/${segments[3]}?ref=symphony`;
}

/** The picture to draw for a Wixel short link (its exported file), or null when `rawUrl` is not one.
 *
 *  Keeps the link's `v` query parameter, and only that one: Symphony stamps `?v=<version>` on the file URL
 *  after the user edits the asset in Wixel, and `/raw` without it is the SAME URL as before the edit, so a
 *  cached old image would keep showing. Every other parameter is still dropped. Mirrors native
 *  `WixelLink.previewUrl` (iOS and Android). */
export function wixelPreviewUrlFromAssetUrl(rawUrl: string | null | undefined): string | null {
  const link = wixelShortLink(rawUrl);
  if (!link) return null;
  const raw = `https://${link.host}/link/${link.slug}/raw`;
  return link.version ? `${raw}?v=${encodeURIComponent(link.version)}` : raw;
}

/** Where "Edit in Wixel" should point for a file's stored content, or null when the file is not a Wixel
 *  asset. An explicit `wixelEditorUrl` wins — it names the real editor deep-link (project + asset), which a
 *  short link only redirects to — and the URL the file points at is the fallback. */
export function wixelEditUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const { header } = parseFileRecordContent(content);
  const explicit = header?.wixelEditorUrl?.trim();
  if (explicit) return explicit;
  return wixelEditUrlFromAssetUrl(extractUrl(content));
}

/** The thumbnail to show for a file's stored content when that file is a Wixel asset, else null. An explicit
 *  `wixelPreviewUrl` wins for the same reason the editor link does — it names a real stored image, where this
 *  is a redirect Wixel resolves per request. */
export function wixelPreviewUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const { header } = parseFileRecordContent(content);
  const explicit = header?.wixelPreviewUrl?.trim();
  if (explicit) return explicit;
  return wixelPreviewUrlFromAssetUrl(extractUrl(content));
}
